import React, { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { Typography, Box, Button, CircularProgress } from '@material-ui/core'
import { useApi } from '~/store/store'
import { ArrowRightIcon } from '~/assets/icons'
import { mutualTheme } from '~/utils/theme'
import { AccountPendenciesCards } from './components'
import { RedirectPJAnalysisStep } from '~/components'
import { useAuthAccount } from '~/store/auth/useAuthAccount'
import { DocumentationOnboardContainer } from '../../components'
import { hasPendingCompleteDataStatus } from '~/utils/analysisStatus'
import { ModalController } from './components/ModalController/ModalController'
import { CompanyAddress, SocialMedia } from '@mutual-services/account-types'
import { trackEvent, OnboardingRegisterDataActions } from '~/service/analytics'

export enum MODAL_TYPE {
  AddressModal,
  ValidateEmailModal,
  ValidatePhoneModal,
  UpdateEmailModal,
  UpdatePhoneModal,
  SocialNetworkInfoModal,
  SuccessValidationPhoneModal,
  SuccessValidationEmailModal
}

export interface AccountPendenciesCachedData {
  email: string,
  phone: string,
  phoneValidated: boolean,
  emailValidated: boolean,
  address: CompanyAddress,
  socialMediaObject: SocialMedia
}

type Payload<T> = T[keyof T]
export type CashedDataPayload = Payload<AccountPendenciesCachedData>

export const AccountPendencies: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [openedModalType, setOpenedModalType] = useState<MODAL_TYPE>(MODAL_TYPE.AddressModal)
  const [completePendencies, completePendenciesState] = useApi('completePendencies')
  const { loading } = completePendenciesState

  const authAccount = useAuthAccount()
  const { email, phone, address, _id: accountId, socialMedia, pendencies, pjAnalysisStatus } = authAccount
  const { status } = pjAnalysisStatus
  const shouldRedirect = useMemo(() => !hasPendingCompleteDataStatus(status), [status])

  const socialMediaObject = {
    facebook: socialMedia?.facebook || '',
    linkedIn: socialMedia?.linkedIn || '',
    instagram: socialMedia?.instagram || '',
    companyWebSite: socialMedia?.companyWebSite || ''
  }

  const [cachedData, setCachedData] = useState<AccountPendenciesCachedData>({
    address,
    email,
    phone,
    socialMediaObject,
    phoneValidated: pendencies?.phone?.status === 'approved',
    emailValidated: pendencies?.email?.status === 'approved'
  })

  const { emailValidated, phoneValidated, address: cachedAddress } = cachedData

  const canAdvanceStep = useMemo(() => phoneValidated && emailValidated && !(cachedAddress === null), [phoneValidated, emailValidated, cachedAddress])

  const advanceStep = async () => {
    const result = await completePendencies({ accountId })

    if (result.ok) {
      trackEvent(OnboardingRegisterDataActions.GO_TO_DOCUMENTATION_PAGE)
      window.location.reload()
      return
    }
    toast.error('Erro ao concluir etapa de Preenchimento de dados. Tente novamente em alguns instantes.', {
      autoClose: 3000,
      closeButton: false,
      position: toast.POSITION.TOP_RIGHT
    })
  }

  if (shouldRedirect) {
    return (
      <RedirectPJAnalysisStep
        status={status}
      />
    )
  }

  const modifyCachedData = (key: keyof AccountPendenciesCachedData, payload: CashedDataPayload) => {
    setCachedData({ ...cachedData, [key]: payload })
  }

  const toggleModal = () => {
    setIsModalVisible(!isModalVisible)
  }

  const onPendencyCardClick = (modalType: MODAL_TYPE) => {
    setOpenedModalType(modalType)
    toggleModal()
  }

  return (
    <DocumentationOnboardContainer
      title='Complete seu cadastro'
      customHeader='Completar dados'
      description='Para continuar sua solicitação, precisamos que você complete as informações abaixo:'
    >
      <AccountPendenciesCards
        cachedData={cachedData}
        onPendencyCardClick={onPendencyCardClick}
      />
      <Box sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end'
      }}>
        <Button
          color='primary'
          variant='contained'
          onClick={advanceStep}
          disabled={!canAdvanceStep || loading}
          sx={{ width: 240, height: 56 }}
          endIcon={loading
            ? <CircularProgress color='inherit' size={20} />
            : <ArrowRightIcon htmlColor={!canAdvanceStep ? mutualTheme.palette.textGray2.main : mutualTheme.palette.common.white} />
          }
        >
          <Typography variant={'airbnbBody1'} sx={{ fontWeight: 'fontWeightMedium' }}>
            Continuar Solicitação
          </Typography>
        </Button>
      </Box>
      <ModalController
        cachedData={cachedData}
        toggleModal={toggleModal}
        isVisible={isModalVisible}
        openedModalType={openedModalType}
        modifyCachedData={modifyCachedData}
        setOpenedModalType={setOpenedModalType}
      />
    </DocumentationOnboardContainer>
  )
}
